/** Persisted server provenance, not a correctness score for the imported files. */
interface ImportAnalysisBase {
  readonly model: 'typesafe/jev-1.13';
  readonly status: 'not-needed' | 'available' | 'unavailable';
  readonly unavailableReason?:
    | 'disabled'
    | 'configuration'
    | 'invalid-input'
    | 'timeout'
    | 'transport'
    | 'invalid-response'
    | 'busy';
  readonly candidatePairs: number;
  readonly evaluatedPairs: number;
  readonly omittedPairs: number;
  readonly modelPairs: number;
  readonly analysisNotice: string | null;
}
export type ImportAnalysis = ImportAnalysisBase &
  (
    | { readonly version: 1 }
    | {
        readonly version: 2;
        readonly cursor: number;
        readonly modelAnsweredPairs: number;
        readonly batchSize: number;
        readonly remainingPairs: number;
      }
  );

export function importAnalysisPresentation(
  workspace: {
    readonly status: 'staged' | 'review' | 'complete';
    readonly analysis?: ImportAnalysis | null;
    readonly proposals: readonly { readonly decision: 'pending' | 'link' | 'separate' }[];
    readonly result?: unknown;
    readonly previewToken?: string | null;
  },
  hasPreview = false,
) {
  const analysis = workspace.analysis;
  const canChangeAnalysis =
    workspace.status === 'review' && !workspace.result && !workspace.previewToken && !hasPreview;
  const remainingPairs = analysis
    ? analysis.version === 2
      ? analysis.remainingPairs
      : analysis.status === 'unavailable'
        ? analysis.candidatePairs
        : analysis.omittedPairs
    : null;
  const continuable = canChangeAnalysis && remainingPairs !== null && remainingPairs > 0;
  const nextBatchSize = Math.min(32, remainingPairs ?? 0);
  const progress = {
    remainingPairs,
    nextBatchSize,
    continuable,
    processedPairs: analysis
      ? analysis.version === 2
        ? analysis.cursor
        : analysis.status === 'unavailable'
          ? 0
          : analysis.evaluatedPairs
      : null,
    answeredPairs: analysis
      ? analysis.version === 2
        ? analysis.modelAnsweredPairs
        : analysis.status === 'available'
          ? analysis.modelPairs
          : 0
      : null,
  };
  const retryable =
    canChangeAnalysis &&
    analysis?.status === 'unavailable' &&
    (analysis.version === 2 ||
      workspace.proposals.every((proposal) => proposal.decision === 'pending'));
  if (!analysis)
    return {
      ...progress,
      retryable: false,
      tone: 'warning' as const,
      title: 'وضعیت تحلیل گزارش نشده است',
      description:
        'برای این ورود، سابقهٔ اجرای تحلیل ذخیره نشده است؛ نبود امتیاز به معنی تأیید صحت اطلاعات نیست.',
    };
  if (analysis.status === 'not-needed')
    return {
      ...progress,
      retryable: false,
      tone: 'info' as const,
      title: 'در این مرحله تحلیل مدل لازم نبود',
      description:
        'در این مرحله ارتباط مبهمی برای ارسال به مدل نبود. تطبیق شناسه‌ها و برخورد کدها به بررسی شما نیاز دارد؛ پاسخ‌های مراحل قبلی نیز حفظ شده‌اند.',
    };
  if (analysis.status === 'available')
    return {
      ...progress,
      retryable: false,
      tone: 'info' as const,
      title: 'پاسخ ساختار یار دریافت شد',
      description:
        'امتیازها از پاسخ مدل دریافت شده‌اند؛ این پاسخ تضمین صحت فایل یا تأیید خودکار ارتباط‌ها نیست.',
    };
  const configuration = ['disabled', 'configuration'].includes(analysis.unavailableReason ?? '');
  return {
    ...progress,
    retryable,
    tone: 'warning' as const,
    title: configuration ? 'ساختار یار فعال یا آماده نیست' : 'ساختار یار در دسترس نبود',
    description: configuration
      ? 'پیکربندی سرویس باید توسط مدیر سامانه بررسی شود. این مرحله پاسخ معتبر مدل ندارد؛ امتیازها و تصمیم‌های مراحل قبلی حفظ شده‌اند.'
      : 'این مرحله پاسخ معتبر مدل ندارد؛ ارتباط‌های مشابه برای بررسی دستی باقی مانده‌اند. امتیازها و تصمیم‌های مراحل قبلی حفظ شده‌اند.',
  };
}
